import PropTypes from 'prop-types';
import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { getAccountTypeDefinitions } from '../../../api/accountTypeDefinition';
import { Select } from '../../../common/components';
import { useSetLoading } from '../../../common/hooks/appearance';
import { apiErrorHandler } from '../../../utils/handlers';
import { asyncDebounce } from '../../../utils/utils';

const AccountTypeDropdown = forwardRef((props, ref) => {
  const {
    selection,
    setSelection,
    pagination,
    dropdownPlaceholder,
    formInput,
    disabled,
    handleSelectFocus,
  } = props;
  const [loading, setLoading] = useState(false);
  const [accTypeDefData, setAccTypeDefData] = useState(null);

  const fetchAccTypeDefData = async (searchVal) => {
    setLoading(true);
    try {
      let params = {};
      if (pagination) params = pagination;

      if (searchVal) params.searchValue = searchVal;

      const response = await getAccountTypeDefinitions(params);
      const formatedResp = response.data.data.map((d) => ({
        value: d.accountTypeId,
        label: d.name,
      }));
      setAccTypeDefData(formatedResp);
      setLoading(false);
      return formatedResp; // this is required for search, loadOptions of Select expects return value
    } catch (err) {
      setLoading(false);
      apiErrorHandler(err);
    }
  };

  useEffect(() => {
    fetchAccTypeDefData();
  }, []);

  useImperativeHandle(ref, () => ({
    fetchAccTypeDefData,
  }));

  const debounceApiCall = asyncDebounce(fetchAccTypeDefData, 1000);

  const handleSelectChange = (selectedOption, triggeredAction) => {
    if (triggeredAction.action === 'clear') {
      // handle logic on click of clear
      fetchAccTypeDefData();
    }
    setSelection(selectedOption);
  };

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  return (
    <div className="common-local-search w-100">
      <i className="fas fa-search" />
      <Select
        name="accountType"
        formInput={formInput}
        classNamePrefix="select-dropdown"
        placeholder={dropdownPlaceholder}
        onChange={handleSelectChange}
        value={selection}
        size="lg"
        isClearable
        cacheOptions
        isAsync
        loadOptions={debounceApiCall}
        defaultOptions={accTypeDefData}
        required
        disabled={disabled}
        onFocus={handleSelectFocus}
      />
    </div>
  );
});

AccountTypeDropdown.propTypes = {
  selection: PropTypes.shape({
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  setSelection: PropTypes.func.isRequired,
  pagination: PropTypes.shape({
    limit: PropTypes.number,
    page: PropTypes.number,
  }),
  dropdownPlaceholder: PropTypes.string,
  formInput: PropTypes.bool,
  disabled: PropTypes.bool,
  handleSelectFocus: PropTypes.func,
};

AccountTypeDropdown.defaultProps = {
  selection: null,
  pagination: null,
  dropdownPlaceholder: 'Search an Account Type',
  formInput: false,
  disabled: false,
  handleSelectFocus: () => {},
};
export default AccountTypeDropdown;
